<template>
	<Dialog :open="isOpen" @update:open="$emit('update:isOpen', $event)">
		<DialogContent
			class="flex h-[90vh] max-h-[800px] w-full max-w-4xl flex-col"
			:disable-overlay-close="true"
			@close="$emit('update:isOpen', false)"
		>
			<DialogHeader>
				<DialogTitle>
					<span class="flex items-center gap-2">
						<span class="fa-light fa-repeat text-blue-500"></span>
						Convert Flow to Template
					</span>
				</DialogTitle>
				<DialogDescription>Create a reusable template from your flow</DialogDescription>
			</DialogHeader>

			<div class="flex flex-1 flex-col overflow-hidden">
				<div class="mb-4 flex items-center justify-between">
					<div class="flex items-center">
						<div v-for="(step, index) in steps" :key="index" class="flex items-center">
							<div
								class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium"
								:class="[
									currentStep > index
										? 'bg-green-500 text-white'
										: currentStep === index
											? 'bg-blue-500 text-white'
											: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
								]"
							>
								{{ index + 1 }}
							</div>
							<div
								v-if="index < steps.length - 1"
								class="h-1 w-8"
								:class="[currentStep > index ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700']"
							></div>
						</div>
					</div>
					<div class="text-sm text-gray-500 dark:text-gray-400">
						Step {{ currentStep + 1 }} of {{ steps.length }}
					</div>
				</div>

				<div class="flex-1 overflow-y-auto">
					<div v-if="currentStep === 0" class="space-y-4">
						<div class="formGroup w-full">
							<p class="filter-label">Template Name</p>
							<input v-model="template.name" type="text" placeholder="Template Name" class="w-full" />
						</div>

						<div class="formGroup w-full">
							<p class="filter-label">Description</p>
							<textarea
								v-model="template.description"
								placeholder="Template Description"
								class="w-full"
								rows="3"
							></textarea>
						</div>
					</div>

					<div v-if="currentStep === 1" class="space-y-4">
						<div class="mb-4">
							<p class="text-sm text-gray-500 dark:text-gray-400">
								Select which flow configuration items should be variables in your template:
							</p>
						</div>

						<div class="rounded-md border border-gray-200 p-4 dark:border-gray-700">
							<div v-if="loading" class="flex items-center justify-center py-8">
								<div
									class="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"
								></div>
							</div>
							<div v-else-if="!flowNodes || flowNodes.length === 0" class="py-8 text-center">
								<p class="text-gray-500 dark:text-gray-400">
									No nodes found in this flow or unable to load node specifications.
								</p>
							</div>
							<div v-else>
								<div class="space-y-2">
									<template v-for="(node, nodeIndex) in flowNodes" :key="nodeIndex">
										<div
											class="rounded-md border border-gray-200 p-3 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
											@click="node.expanded = !node.expanded"
										>
											<div class="flex items-center gap-2">
												<span
													:class="[
														'fa-light',
														node.expanded ? 'fa-minus' : 'fa-plus',
														'text-blue-500'
													]"
												></span>
												<span
													:class="{ 'font-bold': hasSelectedProperties(node) }"
													class="font-medium"
												>
													{{ node.name }} ({{ node.node_id }})
												</span>
											</div>

											<div v-if="node.expanded" class="mt-3 space-y-2 pl-6" @click.stop>
												<div v-if="node.properties && node.properties.length > 0" class="mb-2">
													<button
														class="rounded-md bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
														@click="
															toggleAllNodeProperties(
																node,
																!areAllPropertiesSelected(node)
															)
														"
													>
														{{
															areAllPropertiesSelected(node)
																? 'Deselect All'
																: 'Select All Properties'
														}}
													</button>
												</div>

												<div
													v-if="!node.properties || node.properties.length === 0"
													class="py-2 text-center"
												>
													<p class="text-sm text-gray-500 dark:text-gray-400">
														No templateable fields for this node
													</p>
													<p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
														Node type: {{ node.type }} - Check node configuration
													</p>
												</div>

												<template v-for="(prop, propIndex) in node.properties" :key="propIndex">
													<div class="flex w-full items-center gap-2">
														<input
															:id="`node-${nodeIndex}-prop-${propIndex}`"
															v-model="prop.selected"
															type="checkbox"
															class="h-4 w-4"
														/>
														<label
															:for="`node-${nodeIndex}-prop-${propIndex}`"
															:class="{ 'font-bold': prop.selected }"
															class="w-full"
															:title="prop.placeholder || ''"
														>
															{{ prop.label || prop.name }}
															<span v-if="prop.required" class="text-xs text-red-500"
																>*</span
															>
															<span class="text-xs text-gray-500">
																({{ prop.type }})
															</span>
															<span
																v-if="prop.currentValue !== undefined"
																class="text-xs text-gray-500"
															>
																Current:
																{{ $global.formatObjectProperty(prop.currentValue) }}
															</span>
															<span v-else class="text-xs italic text-gray-400">
																Not set in current flow
															</span>

															<span
																v-if="prop.type === 'select' && prop.options"
																class="ml-2 text-xs text-blue-500"
															>
																Options:
																{{ prop.options.map((o) => o.label).join(', ') }}
															</span>

															<span
																v-if="prop.group && prop.group !== 'General'"
																class="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300"
															>
																{{ prop.group }}
															</span>
														</label>
													</div>
												</template>
											</div>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>

					<div v-if="currentStep === 2" class="space-y-4">
						<div
							class="overflow-hidden rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
						>
							<h3 class="mb-2 text-lg font-medium dark:text-white">Template Summary</h3>

							<div class="space-y-4 overflow-y-auto">
								<div>
									<p class="text-sm text-gray-500 dark:text-gray-400">Name:</p>
									<p class="break-words font-medium dark:text-white">{{ template.name }}</p>
								</div>

								<div>
									<p class="text-sm text-gray-500 dark:text-gray-400">Description:</p>
									<p class="break-words dark:text-white">{{ template.description || 'Not set' }}</p>
								</div>

								<div>
									<p class="text-sm text-gray-500 dark:text-gray-400">Variables:</p>
									<p class="dark:text-white">{{ selectedVariablesCount }} variables selected</p>
									<div class="mt-3">
										<div v-for="(node, nodeIndex) in flowNodes" :key="nodeIndex" class="mb-4">
											<div v-if="hasSelectedProperties(node)">
												<p class="break-words font-medium">{{ node.name }} ({{ node.type }})</p>

												<div class="ml-2 mt-1 sm:ml-4">
													<div
														v-for="(prop, propIndex) in node.properties"
														:key="propIndex"
														class="mb-2"
													>
														<div
															v-if="prop.selected"
															class="flex flex-wrap items-start gap-1 sm:gap-2"
														>
															<span
																class="fa-light fa-check mt-0.5 flex-shrink-0 text-green-500"
															></span>
															<span class="break-words">{{
																prop.label || prop.name
															}}</span>
															<span
																v-if="prop.required"
																class="flex-shrink-0 text-xs text-red-500"
																>*</span
															>
															<span class="flex-shrink-0 text-xs text-gray-500"
																>({{ prop.type }})</span
															>
															<span
																v-if="prop.options"
																class="flex-shrink-0 text-xs text-blue-500"
															>
																[{{ prop.options.length }} options]
															</span>
															<span
																v-if="prop.group && prop.group !== 'General'"
																class="flex-shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300"
															>
																{{ prop.group }}
															</span>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="mt-4 flex items-center">
							<input
								id="delete-flow"
								v-model="deleteFlowAfterCreation"
								type="checkbox"
								class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
							/>
							<label for="delete-flow" class="ml-2 text-sm text-gray-700 dark:text-gray-300">
								Delete the original flow after template creation
							</label>
						</div>
					</div>
				</div>
			</div>

			<DialogFooter class="mt-4 flex flex-row justify-between border-t pt-4">
				<div class="flex gap-2">
					<button v-if="currentStep > 0" class="jmiBtn gray" @click="currentStep--">Back</button>
					<button class="jmiBtn red" @click="$emit('update:isOpen', false)">Cancel</button>
				</div>
				<div>
					<button
						v-if="currentStep < steps.length - 1"
						class="jmiBtn blue"
						:disabled="!canProceed()"
						@click="currentStep++"
					>
						Next
					</button>
					<button v-else class="jmiBtn green" :disabled="!canCreate()" @click="createTemplate()">
						Create Template
					</button>
				</div>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>

<script>
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog'
import flowsApi from '../scripts/flows/flows.api'
import flowTemplatesApi from '../scripts/flowtemplates/flowtemplates.api'

export default {
	components: {
		Dialog,
		DialogContent,
		DialogHeader,
		DialogTitle,
		DialogDescription,
		DialogFooter
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		flowId: {
			type: [String, Number, null],
			required: true
		}
	},

	emits: ['update:isOpen', 'template-created', 'reload-flows'],

	data() {
		return {
			steps: [
				{ title: 'Template Details', description: 'Enter basic information about your template' },
				{ title: 'Variable Selection', description: 'Choose which fields should be configurable' },
				{ title: 'Preview', description: 'Review your template before creating it' }
			],
			currentStep: 0,
			template: {
				name: '',
				description: '',
				baseFlowId: null,
				variables: []
			},
			flow: null,
			flowNodes: [],
			loading: false,
			nodeSpecs: [],
			deleteFlowAfterCreation: false
		}
	},

	computed: {
		selectedVariablesCount() {
			let count = 0

			if (!this.flowNodes) return 0

			this.flowNodes.forEach((node) => {
				if (!node.properties) return

				node.properties.forEach((prop) => {
					if (prop.selected) {
						count++
					}
				})
			})

			return count
		}
	},

	watch: {
		flowId: {
			immediate: true,
			handler(newId, oldId) {
				if (newId && this.isOpen && newId !== oldId) {
					this.loadFlowData()
				}
			}
		},
		isOpen: {
			handler(isOpen) {
				if (isOpen && this.flowId) {
					this.resetState()
					this.loadFlowData()
				}
			}
		}
	},

	created() {
		flowsApi
			.getAllNodes()
			.then((response) => {
				if (Array.isArray(response)) {
					this.nodeSpecs = response
				} else if (response && response.data && Array.isArray(response.data)) {
					this.nodeSpecs = response.data
				}
			})
			.catch((error) => {
				console.error('Failed to fetch node specifications', error)
			})
	},

	methods: {
		resetState() {
			this.currentStep = 0
			this.flowNodes = []
			this.template = {
				name: '',
				description: '',
				baseFlowId: null,
				variables: []
			}
		},

		hasSelectedProperties(node) {
			if (!node || !node.properties) return false
			return node.properties.some((prop) => prop.selected)
		},

		formatLabel(key) {
			return key
				.replace(/([A-Z])/g, ' $1')
				.replace(/_/g, ' ')
				.replace(/^\w/, (c) => c.toUpperCase())
				.trim()
		},

		async loadFlowData() {
			try {
				this.loading = true
				this.flowNodes = []
				this.nodeSpecs = []

				try {
					const specResponse = await flowsApi.getAllNodes()

					if (Array.isArray(specResponse)) {
						this.nodeSpecs = specResponse
					} else if (specResponse && specResponse.data && Array.isArray(specResponse.data)) {
						this.nodeSpecs = specResponse.data
					} else {
						this.nodeSpecs = []
					}
				} catch (error) {
					this.nodeSpecs = []
				}

				try {
					const response = await flowsApi.getById(this.flowId)

					if (!response || !response.data) {
						throw new Error('Failed to load flow data')
					}

					this.flow = response.data
					this.template.name = this.flow.name
					this.template.description = this.flow.description || ''
					this.template.baseFlowId = this.flow.id

					if (this.flow.data && this.flow.data.nodes) {
						let tempFlowNodes = []

						if (Array.isArray(this.flow.data.nodes)) {
							tempFlowNodes = this.flow.data.nodes
								.filter((node) => node.type !== 'entrypoint')
								.map((node) => this.processNode(node))
						} else {
							tempFlowNodes = Object.keys(this.flow.data.nodes)
								.map((nodeId) => {
									const node = this.flow.data.nodes[nodeId]
									if (node.type === 'entrypoint') return null
									return this.processNode({ ...node, id: nodeId })
								})
								.filter((node) => node !== null)
						}

						this.flowNodes = tempFlowNodes
						console.log('flowNodes', this.flowNodes)
					} else {
						this.flowNodes = []
					}
				} catch (error) {
					this.flowNodes = []
				}
			} catch (error) {
				this.flowNodes = []
			} finally {
				this.loading = false
			}
		},

		processNode(node) {
			if (!node || !node.type) {
				return {
					id: node?.id || 'unknown',
					name: node?.name || 'Unknown Node',
					type: node?.type || 'unknown',
					expanded: false,
					properties: [],
					node_id: node?.config?.node_id || node.type || 'unknown'
				}
			}

			let properties = []
			if (node.config && Object.keys(node.config).length > 0) {
				properties = Object.keys(node.config)
					.filter((key) => key !== 'node_id')
					.map((key) => {
						const value = node.config[key]
						return {
							name: key,
							label: this.formatLabel(key),
							type: typeof value,
							currentValue: value,
							required: false,
							group: 'Current Configuration',
							selected: false
						}
					})
			}

			if (!this.nodeSpecs || !Array.isArray(this.nodeSpecs) || this.nodeSpecs.length === 0) {
				return {
					id: node.id,
					name: node.name || node.id,
					type: node.type,
					expanded: false,
					properties,
					node_id: node?.config?.node_id || node.type || 'unknown'
				}
			}

			const nodeSpec = this.nodeSpecs.find((spec) => spec.type === node.type)

			if (nodeSpec) {
				if (nodeSpec.properties && Array.isArray(nodeSpec.properties)) {
					const specProperties = nodeSpec.properties.map((propSpec) => {
						const currentValue =
							node.config && propSpec.name in node.config ? node.config[propSpec.name] : undefined

						return {
							name: propSpec.name,
							label: propSpec.label || this.formatLabel(propSpec.name),
							type: propSpec.type || typeof currentValue,
							currentValue: currentValue,
							required: propSpec.required || false,
							group: propSpec.group || 'General',
							placeholder: propSpec.placeholder || '',
							options: propSpec.options || null,
							selected: false
						}
					})

					const specPropertyNames = specProperties.map((p) => p.name)
					const configOnlyProperties = properties.filter((p) => !specPropertyNames.includes(p.name))
					properties = [...specProperties, ...configOnlyProperties]
				}
			}

			return {
				id: node.id,
				name: node.name || node.id,
				type: node.type,
				expanded: false,
				properties,
				node_id: node?.config?.node_id || node.type || 'unknown'
			}
		},

		toggleAllNodeProperties(node, checked) {
			if (!node || !node.properties) return

			node.properties.forEach((prop) => {
				prop.selected = checked
			})
		},

		areAllPropertiesSelected(node) {
			if (!node || !node.properties || node.properties.length === 0) return false
			return node.properties.every((prop) => prop.selected)
		},

		canProceed() {
			if (this.currentStep === 0) {
				return this.template.name.trim() !== ''
			} else if (this.currentStep === 1) {
				const hasNodesWithProperties = this.flowNodes.some(
					(node) => node.properties && node.properties.length > 0
				)

				if (!hasNodesWithProperties) {
					console.warn('No nodes with properties found, but allowing to proceed')
					return true
				}

				return true
			}
			return true
		},

		canCreate() {
			if (!this.template.name.trim()) {
				return false
			}

			const hasNodesWithProperties = this.flowNodes.some((node) => node.properties && node.properties.length > 0)

			if (!hasNodesWithProperties) {
				console.warn('No nodes with properties found, but allowing template creation')
				return true
			}

			return this.selectedVariablesCount > 0
		},

		async createTemplate() {
			try {
				this.loading = true
				console.log('Creating template with deleteFlowAfterCreation:', this.deleteFlowAfterCreation)

				const templateData = {
					name: this.template.name,
					description: this.template.description,
					original_flow_id: this.flow.id,
					flow_spec: this.flow.data,
					template_fields: this.generateTemplateFields(),
					nodeConfig: this.generateNodeConfig()
				}

				console.log('Template data:', templateData)
				const response = await flowTemplatesApi.create(templateData)
				console.log('Template creation response:', response)

				if (response) {
					let templateData = null

					if (response.data) {
						templateData = response.data
					} else if (response.identifiers && response.identifiers.length > 0) {
						templateData = {
							id: response.identifiers[0].id,
							name: this.template.name,
							description: this.template.description
						}
					} else {
						templateData = {
							id: Date.now(),
							name: this.template.name,
							description: this.template.description
						}
					}

					if (this.deleteFlowAfterCreation && this.flowId) {
						console.log('Attempting to delete flow with ID:', this.flowId)
						try {
							const deleteResponse = await flowsApi.delete(this.flowId)
							console.log('Flow deletion response:', deleteResponse)
							console.log('Original flow deleted successfully')

							this.$emit('reload-flows')
						} catch (error) {
							console.error('Error deleting original flow:', error)

							this.$swal({
								title: 'Warning',
								text: 'Template was created but we could not delete the original flow.',
								icon: 'warning'
							})
						}
					}

					this.$emit('template-created', templateData)
					this.$emit('update:isOpen', false)

					this.$emit('reload-flows')

					this.resetState()
				}
			} catch (error) {
				console.error('Error creating template:', error)
				this.$swal({
					title: 'Error',
					text: 'Failed to create template. Please try again.',
					icon: 'error'
				})
			} finally {
				this.loading = false
			}
		},

		generateTemplateFields() {
			const templateFields = {}

			this.flowNodes.forEach((node) => {
				if (!node || !node.properties) return

				const nodeKey = `${node.type}_${node.node_id}`
				templateFields[nodeKey] = []

				node.properties.forEach((prop) => {
					if (prop.selected) {
						templateFields[nodeKey].push(prop.name)
					}
				})

				if (templateFields[nodeKey].length === 0) {
					delete templateFields[nodeKey]
				}
			})

			return templateFields
		},

		generateNodeConfig() {
			const nodeConfig = {}

			this.flowNodes.forEach((node) => {
				if (!node || !node.properties) return

				const nodeKey = `${node.type}_${node.node_id}`
				nodeConfig[nodeKey] = {}

				node.properties.forEach((prop) => {
					if (prop.selected && prop.currentValue !== undefined) {
						nodeConfig[nodeKey][prop.name] = prop.currentValue
					}
				})

				if (Object.keys(nodeConfig[nodeKey]).length === 0) {
					delete nodeConfig[nodeKey]
				}
			})

			return nodeConfig
		}
	}
}
</script>
